/**
 * Cross-Variant Pricing-Integrity helpers (BLUE / WHITE / GOLD CRUMPLE).
 *
 * The Missing-Sticker flow keeps a customer-facing "originalLines" list
 * (the list the customer pasted) so unchecked rows can be shown with a
 * strike-through and later restored. Historically, every time the
 * customer toggled an item the backend was re-quoted for the fresh
 * selection, but the visible per-line prices kept the ORIGINAL unit
 * prices frozen at paste-time. When a toggle crossed a quantity
 * threshold (e.g. 35 → 34 or 34 → 35) the totals updated but the
 * visible line prices stayed at the wrong tier, so what the customer
 * saw did NOT match what they were being charged.
 *
 * Every helper in this file is a pure function so it can be unit-tested
 * without dragging React or axios into Jest.
 */

/**
 * Merge fresh unit_price / line_total / display_category / availability
 * from a re-quoted `quote.lines[]` back onto the frozen `originalLines`
 * list, keyed by canonical `code`. Rows that are not in the fresh
 * quote (i.e. currently deselected) keep whatever they had previously
 * but ALSO absorb any new pricing tier so if the user re-selects them
 * the visible price is already correct.
 *
 * Callers must always replace `originalLines` with the return value —
 * NEVER mutate the fresh quote object.
 *
 * @param {Array} originalLines - the frozen display order
 * @param {Array} freshLines - `quote.lines` from a fresh /orders/quote
 * @param {Object} tierPriceOverrides - {code: {unit_price, line_total?}}
 *   applied to rows NOT in freshLines. Used when the caller wants
 *   deselected rows to update to the current tier too (recommended).
 * @returns {Array} new list, preserving the original display order
 */
export function mergeFreshPrices(originalLines, freshLines, tierPriceOverrides = {}) {
  const freshByCode = new Map();
  for (const ln of freshLines || []) {
    if (ln && ln.code) freshByCode.set(String(ln.code), ln);
  }
  return (originalLines || []).map((orig) => {
    if (!orig || !orig.code) return orig;
    const fresh = freshByCode.get(String(orig.code));
    if (fresh) {
      // Fresh row wins for money-critical fields. Preserve non-money
      // metadata (availability, subject_name, etc.) present on either
      // side.
      return {
        ...orig,
        ...fresh,
        // Availability is enriched from the backend availability
        // service — keep whichever side has the freshest map.
        availability: fresh.availability || orig.availability || null,
      };
    }
    // Deselected row — still apply tier-level price overrides so if
    // the customer re-checks it the visible price is already correct.
    const override = tierPriceOverrides[String(orig.code)];
    if (override && typeof override.unit_price === "number") {
      const qty = Number(orig.qty || 0);
      const unit = Number(override.unit_price);
      return {
        ...orig,
        unit_price: unit,
        line_total: typeof override.line_total === "number"
          ? Number(override.line_total)
          : Number((unit * qty).toFixed(2)),
      };
    }
    return orig;
  });
}

/**
 * Cross-check that a rendered quote (`originalLines` view + `quote.totals`)
 * is internally consistent: the sum of visible SELECTED line totals plus
 * the surcharge, handling, shipping and tax equal the snapshot total.
 *
 * Returns `{ ok: true }` on match or `{ ok: false, expected, actual, delta,
 * reason }` on mismatch. Callers should BLOCK submit and trigger an
 * auto-refetch when `ok` is false.
 *
 * @param {Object} args
 * @param {Array} args.lines - the rendered originalLines list
 * @param {Function} args.isSelected - (code) => boolean
 * @param {Object} args.totals - `quote.totals`
 * @param {number} [args.tolerance=0.01] - cents-scale drift tolerance
 * @returns {Object}
 */
export function validatePricingInvariant({ lines, isSelected, totals, tolerance = 0.01 }) {
  const t = totals || {};
  const visibleSelectedSubtotal = (lines || [])
    .filter((ln) => ln && ln.code && isSelected(ln.code))
    .reduce((s, ln) => s + Number(ln.line_total || 0), 0);
  const backendSubtotal = Number(t.subtotal || 0);
  // Guard 1: visible-line subtotal must match the snapshot subtotal.
  if (Math.abs(visibleSelectedSubtotal - backendSubtotal) > tolerance) {
    return {
      ok: false,
      reason: "visible_subtotal_mismatch",
      expected: backendSubtotal,
      actual: Number(visibleSelectedSubtotal.toFixed(2)),
      delta: Number((visibleSelectedSubtotal - backendSubtotal).toFixed(2)),
    };
  }
  // Guard 2: subtotal + surcharge + handling + shipping + tax == total.
  const surcharge = Number(t.small_order_surcharge?.amount || 0);
  const handling  = Number(t.handling_fee || 0);
  const shipping  = Number(t.shipping?.cost || 0);
  const tax       = Number(t.tax?.amount || 0);
  const expectedTotal = Number((backendSubtotal + surcharge + handling + shipping + tax).toFixed(2));
  const actualTotal   = Number(t.total || 0);
  if (Math.abs(expectedTotal - actualTotal) > tolerance) {
    return {
      ok: false,
      reason: "totals_reconciliation_mismatch",
      expected: expectedTotal,
      actual: actualTotal,
      delta: Number((expectedTotal - actualTotal).toFixed(2)),
    };
  }
  return { ok: true };
}
